import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navigation = () => {
  const { language, changeLanguage, t, supportedLanguages } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const location = useLocation();
  const langRef = React.useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const onClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const ALL_LANGUAGES = [
    { code: 'nl', label: 'Nederlands', short: 'NL' },
    { code: 'en', label: 'English', short: 'EN' },
    { code: 'fr', label: 'Français', short: 'FR' },
  ];
  // Only show languages the site currently supports (configured in LanguageContext)
  const LANGUAGES = ALL_LANGUAGES.filter((l) => supportedLanguages.includes(l.code));
  const currentLang = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      // Hide categories that currently have no products
      // (e.g., when the only partner in a category ends its affiliate program).
      // They come back automatically as soon as a new product is added.
      const withProducts = (response.data || []).filter(
        (c) => c.product_count === undefined || c.product_count === null || c.product_count > 0
      );
      setCategories(withProducts);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Row: Logo + Home + Language */}
        <div className="flex justify-between items-center h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 shrink-0" data-testid="logo-link">
            <img
              src="/uandfashion-logo.png"
              alt="U&Fashion logo"
              width="32"
              height="32"
              className="h-8 w-8 rounded-md shadow-sm"
            />
            <div className="text-2xl font-bold bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 bg-clip-text text-transparent">
              U&Fashion
            </div>
          </Link>

          {/* Desktop: Home + Language */}
          <div className="hidden md:flex items-center space-x-5">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors hover:text-pink-500 ${
                location.pathname === '/' ? 'text-pink-500' : 'text-gray-700'
              }`}
              data-testid="nav-home"
            >
              {t('Home', 'Home', 'Accueil')}
            </Link>

            <Link
              to="/acties"
              className={`text-sm font-medium transition-colors hover:text-pink-500 inline-flex items-center gap-1.5 ${
                location.pathname === '/acties' ? 'text-pink-500' : 'text-pink-600'
              }`}
              data-testid="nav-action-alerts"
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-pink-500 animate-pulse" aria-hidden="true" />
              {t('Acties', 'Action Alerts', 'Promotions')}
            </Link>

            <Link
              to="/inspiratie"
              className={`text-sm font-medium transition-colors hover:text-pink-500 ${
                location.pathname.startsWith('/inspiratie') ? 'text-pink-500' : 'text-gray-500'
              }`}
              data-testid="nav-inspiration"
            >
              {t('Inspiratie', 'Inspiration', 'Inspiration')}
            </Link>

            <Link
              to="/for-brands"
              className={`text-sm font-medium transition-colors hover:text-pink-500 ${
                location.pathname === '/for-brands' ? 'text-pink-500' : 'text-gray-500'
              }`}
              data-testid="nav-for-brands"
            >
              {t('Voor Merken', 'For Brands', 'Pour les marques')}
            </Link>

            <div className="relative" ref={langRef}>
              <button
                onClick={() => setIsLangOpen((o) => !o)}
                className="flex items-center space-x-1.5 px-3 py-1.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-md text-sm font-medium hover:shadow-md transform hover:scale-105 transition-all duration-300"
                data-testid="language-toggle"
                aria-haspopup="listbox"
                aria-expanded={isLangOpen}
              >
                <Globe className="w-4 h-4" />
                <span>{currentLang.short}</span>
                <svg className={`w-3 h-3 transition-transform ${isLangOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {isLangOpen && (
                <ul
                  role="listbox"
                  className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-50"
                  data-testid="language-dropdown"
                >
                  {LANGUAGES.map((lang) => (
                    <li key={lang.code}>
                      <button
                        onClick={() => { changeLanguage(lang.code); setIsLangOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-pink-50 hover:text-pink-600 transition-colors ${
                          lang.code === language ? 'bg-pink-50 text-pink-600 font-semibold' : 'text-gray-700'
                        }`}
                        data-testid={`language-option-${lang.code}`}
                      >
                        <span className="inline-block w-7 text-xs uppercase tracking-wider">{lang.short}</span>
                        <span>{lang.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-pink-500 hover:bg-gray-100"
            data-testid="mobile-menu-button"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Second Row: All Categories (Desktop) */}
        {categories.length > 0 && (
          <div
            className="hidden md:flex items-center gap-x-4 gap-y-1 flex-wrap border-t border-gray-100 py-2"
            data-testid="desktop-categories-row"
          >
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className={`text-[13px] font-medium whitespace-nowrap transition-colors hover:text-pink-500 ${
                  location.pathname === `/category/${category.slug}`
                    ? 'text-pink-500'
                    : 'text-gray-700'
                }`}
                data-testid={`nav-${category.slug}`}
              >
                {(language === 'nl' && category.name_nl) ||
                  (language === 'fr' && (category.name_fr || category.name_en)) ||
                  category.name_en}
              </Link>
            ))}
          </div>
        )}

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden pb-4" data-testid="mobile-menu">
            <div className="flex flex-col space-y-3">
              {/* Language picker on mobile — 3 segmented buttons */}
              <div className="flex bg-gradient-to-r from-pink-500 to-purple-500 rounded-lg p-1">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsMenuOpen(false);
                    }}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold transition-all ${
                      language === lang.code
                        ? 'bg-white text-purple-600 shadow-sm'
                        : 'text-white/90 hover:bg-white/10'
                    }`}
                    data-testid={`mobile-language-${lang.code}`}
                  >
                    {lang.short}
                  </button>
                ))}
              </div>

              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className="text-sm font-medium text-gray-700 hover:text-pink-500 transition-colors"
              >
                {t('Home', 'Home', 'Accueil')}
              </Link>
              <Link
                to="/acties"
                onClick={() => setIsMenuOpen(false)}
                className="text-sm font-medium text-pink-600 hover:text-pink-500 transition-colors inline-flex items-center gap-1.5"
                data-testid="mobile-nav-action-alerts"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-pink-500 animate-pulse" aria-hidden="true" />
                {t('Acties', 'Action Alerts', 'Promotions')}
              </Link>
              <Link
                to="/inspiratie"
                onClick={() => setIsMenuOpen(false)}
                className="text-sm font-medium text-gray-700 hover:text-pink-500 transition-colors"
                data-testid="mobile-nav-inspiration"
              >
                {t('Inspiratie', 'Inspiration', 'Inspiration')}
              </Link>
              <Link
                to="/for-brands"
                onClick={() => setIsMenuOpen(false)}
                className="text-sm font-medium text-gray-700 hover:text-pink-500 transition-colors"
                data-testid="mobile-nav-for-brands"
              >
                {t('Voor Merken', 'For Brands', 'Pour les marques')}
              </Link>
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/category/${category.slug}`}
                  onClick={() => setIsMenuOpen(false)}
                  className="text-sm font-medium text-gray-700 hover:text-pink-500 transition-colors"
                >
                  {(language === 'nl' && category.name_nl) ||
                    (language === 'fr' && (category.name_fr || category.name_en)) ||
                    category.name_en}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
